import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { RENTER_INVITE_QUERY_PARAM } from '@/constants/Constants';
import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';

const addInviteQueryParam = (params: URLSearchParams) => {
  const nextParams = new URLSearchParams(params);
  nextParams.set(RENTER_INVITE_QUERY_PARAM, 'true');
  return nextParams;
};

const removeInviteQueryParam = (params: URLSearchParams) => {
  const nextParams = new URLSearchParams(params);
  nextParams.delete(RENTER_INVITE_QUERY_PARAM);
  return nextParams;
};

// Bidirectionally syncs the renter-invite drawer state and `?renter-invite=true`:
//
//  - Drawer opens (programmatically) and the param isn't in the URL → PUSH the param entry,
//    so Back peels off the drawer-open state instead of navigating away from the page.
//  - Drawer closes via the X button (we own the top history entry) → navigate(-1) to pop the
//    pushed param entry. If we don't own it (e.g., the page was opened via a deep link with the
//    param), fall back to replacing the param out.
//  - Param disappears from the URL while the drawer is open (Back button or external nav) →
//    close the drawer state to match.
export const useInviteDrawerQueryParamSync = () => {
  const isOpen = useInviteLeaseDetailsDrawerStore((state) => state.isOpen);
  const closeDrawer = useInviteLeaseDetailsDrawerStore((state) => state.closeDrawer);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const closeDrawerRef = useRef(closeDrawer);
  closeDrawerRef.current = closeDrawer;

  const paramIsSet = searchParams.get(RENTER_INVITE_QUERY_PARAM) === 'true';

  const prevOpenRef = useRef(isOpen);
  const prevParamRef = useRef(paramIsSet);
  // Tracks whether THIS hook pushed the current ?renter-invite=true entry. If true, closing the
  // drawer should pop the entry; if false, the param entry was already on the URL when we arrived
  // (deep link / redirect) and closing should just strip the param.
  const pushedParamEntryRef = useRef(false);

  useEffect(() => {
    const openTransition = isOpen && !prevOpenRef.current;
    const closeTransition = !isOpen && prevOpenRef.current;
    const paramRemovedExternally =
      !paramIsSet && prevParamRef.current && !openTransition && !closeTransition;

    if (openTransition && !paramIsSet) {
      setSearchParamsRef.current(addInviteQueryParam);
      pushedParamEntryRef.current = true;
    }

    if (closeTransition && paramIsSet) {
      if (pushedParamEntryRef.current) {
        navigateRef.current(-1);
      } else {
        setSearchParamsRef.current(removeInviteQueryParam, { replace: true });
      }
      pushedParamEntryRef.current = false;
    }

    if (paramRemovedExternally && isOpen) {
      closeDrawerRef.current();
      pushedParamEntryRef.current = false;
    }

    prevOpenRef.current = isOpen;
    prevParamRef.current = paramIsSet;
  }, [isOpen, paramIsSet]);

  // Remove param on unmount in case the drawer was open during a route change.
  useEffect(() => {
    return () => {
      if (prevOpenRef.current && prevParamRef.current) {
        setSearchParamsRef.current(removeInviteQueryParam, { replace: true });
      }
    };
  }, []);
};
